// VMS façade（plan/vms-system-v0.md §4、§5.1、§5.2）：共同根 {api}/v1/homes/{home}/vms。
// 寫入一律帶 Idempotency-Key（同一個操作重送用同一把 key，平台回同一個結果）；列表是穩定 cursor（15 分鐘）。
// 即時觀看：先 POST live-sessions 拿 session_ticket，再在 WSS start_viewing 帶上（LiveView 的 reserve 選項）。
import { kinioFetch } from './auth';
import { planeBase } from './config';

/**
 * façade 的設備（SPEC #terms：device_ref 一律是字串——原生 = device_id、子設備 = `<parent>:chN`；
 * 通道另給 channel，不是通道為 null）。
 */
export interface VmsFacadeDevice {
  device_ref: string;
  channel?: number | null;
  name?: string;
  parent?: { device_ref: string } | null;
  kind?: string;
  model?: string;
  capabilities?: string[];
  /** AID 型態碼（vms-web §5.19.1；例 KST = Kinio Station） */
  type_code?: string | null;
  /** product_models 的 device_class（依 model；查不到為 null） */
  device_class?: string | null;
  /** shadow reported 的 cluster 名（WSS 的設備靠它看能力；子設備為 null） */
  reported_clusters?: string[] | null;
  presence?: { state?: string; last_seen_at?: string; reason?: string } | string;
  media_status?: string;
  scope?: Record<string, unknown>;
  [k: string]: unknown;
}

export interface VmsPage<T> {
  data: T[];
  next_cursor: string | null;
  has_more?: boolean;
  source_status?: Record<string, unknown>;
}

export interface VmsClip {
  source: 'cloud' | 'local' | 'backup';
  clip_id: string;
  start_at: string;
  end_at: string;
  duration_ms?: number;
  container?: string;
  has_event?: boolean;
  event_ids?: string[];
  has_thumbnail?: boolean;
  /** 多鏡頭（0 可省略）。 */
  lens?: number;
}

export interface VmsTimeline {
  from: string;
  to: string;
  resolution_seconds: number;
  tz: string;
  buckets: { start: string; seconds?: number; kinds?: string[]; [k: string]: unknown }[];
  [k: string]: unknown;
}

/** MyCam 電腦端對一台攝影機的錄影覆蓋（vms-web §5.19.3）。 */
export interface VmsRecorderCoverage {
  node: { device_ref: string; presence?: string };
  from: string;
  to: string;
  gaps: { from: string; to: string; reason?: string }[];
  known_until: string;
}

export interface VmsPlaybackSession {
  session_id: string;
  state: string;
  source: string;
  requested_source: string;
  expires_at: string;
  playback: { kind: 'hls'; playlist_url: string } | { kind: 'clip'; url: string };
}

export interface VmsLiveSession {
  session_id: string;
  state?: string;
  expires_at?: string;
  signaling?: { url: string; protocol: string; home_id: string; command: string; device_ref: string; session_ticket: string };
  [k: string]: unknown;
}

export interface VmsFacadeEvent {
  event_id: string;
  revision?: number;
  key: string;
  class?: string | null;
  phase?: 'instant' | 'started' | 'ended';
  thing?: { thing_id?: string; device_ref?: string; channel?: number | null; lens?: number | null };
  occurred_at: string;
  fields?: Record<string, unknown>;
  media_state?: string;
  [k: string]: unknown;
}

/** 一次操作一把 Idempotency-Key（crypto.randomUUID；舊環境退回時間 + 亂數）。 */
export function idempotencyKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function vmsUrl(api: string, homeId: string, path: string, q?: Record<string, string | number | undefined | null>): string {
  let u = `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/vms${path}`;
  const qs = Object.entries(q ?? {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  if (qs) u += '?' + qs;
  return u;
}

const dev = (d: string) => '/devices/' + encodeURIComponent(d);

export const vms = {
  devices: (api: string, homeId: string, cursor?: string) => kinioFetch<VmsPage<VmsFacadeDevice>>(vmsUrl(api, homeId, '/devices', { cursor })),
  /** 全部設備（跟著 cursor 翻完）。 */
  async allDevices(api: string, homeId: string): Promise<VmsFacadeDevice[]> {
    const out: VmsFacadeDevice[] = [];
    let cur: string | undefined;
    for (let i = 0; i < 50; i++) {
      const p = await vms.devices(api, homeId, cur);
      out.push(...(p?.data ?? []));
      if (!p?.next_cursor) break;
      cur = p.next_cursor;
    }
    return out;
  },
  // 多鏡頭（plan SPEC #multi-lens-recording）：recordings / timeline / recording-days / thumbnail / playback-sessions 帶 `lens`（0–7、沒帶 = 0）。
  recordings: (api: string, homeId: string, device: string, q: { from: string; to: string; source?: string; limit?: number; cursor?: string; lens?: number }) =>
    kinioFetch<VmsPage<VmsClip>>(vmsUrl(api, homeId, dev(device) + '/recordings', q)),
  timeline: (api: string, homeId: string, device: string, q: { from: string; to: string; resolution_seconds?: number; tz?: string; lens?: number }) =>
    kinioFetch<VmsTimeline>(vmsUrl(api, homeId, dev(device) + '/timeline', q)),
  recorderCoverage: (api: string, homeId: string, device: string, q: { from: string; to: string }) =>
    kinioFetch<{ data: VmsRecorderCoverage[] }>(vmsUrl(api, homeId, dev(device) + '/recorder-coverage', q)),
  playbackSession: (
    api: string,
    homeId: string,
    device: string,
    body: { source?: string; clip_id?: string; from?: string; to?: string },
    key = idempotencyKey(),
    lens?: number,
  ) =>
    kinioFetch<VmsPlaybackSession>(vmsUrl(api, homeId, dev(device) + '/playback-sessions', { lens: lens || undefined }), {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': key },
    }),
  download: (api: string, homeId: string, device: string, clip: string, key = idempotencyKey()) =>
    kinioFetch<{ url: string; expires_at: string }>(vmsUrl(api, homeId, dev(device) + '/recordings/' + encodeURIComponent(clip) + '/download'), {
      method: 'POST',
      headers: { 'Idempotency-Key': key },
    }),
  recordingPolicy: (api: string, homeId: string, device: string) => kinioFetch<Record<string, unknown>>(vmsUrl(api, homeId, dev(device) + '/recording-policy')),
  setRecordingPolicy: (api: string, homeId: string, device: string, body: Record<string, unknown>, key = idempotencyKey()) =>
    kinioFetch<Record<string, unknown>>(vmsUrl(api, homeId, dev(device) + '/recording-policy'), { method: 'PUT', body, headers: { 'Idempotency-Key': key } }),
  /** `lens`：沒帶 = 只回主鏡頭的事件、`'all'` = 全部（每筆 thing.lens）。 */
  events: (api: string, homeId: string, q: { from?: string; to?: string; device?: string; class?: string; limit?: number; cursor?: string; lens?: number | 'all' }) =>
    kinioFetch<VmsPage<VmsFacadeEvent>>(vmsUrl(api, homeId, '/events', q)),
  /** 多鏡頭：`lens`（0–7）、`primary_session_id`（雙畫面伴隨，不合 409 COMPANION_INVALID）。 */
  liveSession: (api: string, homeId: string, device: string, body: { profile?: string; lens?: number; primary_session_id?: string } = {}, key = idempotencyKey()) =>
    kinioFetch<VmsLiveSession>(vmsUrl(api, homeId, dev(device) + '/live-sessions'), { method: 'POST', body, headers: { 'Idempotency-Key': key } }),
  session: (api: string, homeId: string, id: string) => kinioFetch<VmsLiveSession>(vmsUrl(api, homeId, '/sessions/' + encodeURIComponent(id))),
  endSession: (api: string, homeId: string, id: string) => kinioFetch(vmsUrl(api, homeId, '/sessions/' + encodeURIComponent(id)), { method: 'DELETE' }),
};

/** LiveView 的 reserve：每次開始觀看前向 façade 預留一個 session、拿一次性 ticket。 */
export function vmsReserve(
  api: string,
  homeId: string,
  device: string,
  profile?: string,
  lens?: { lens?: number; primarySessionId?: string },
): () => Promise<{ ticket: string; sessionId: string }> {
  return async () => {
    const s = await vms.liveSession(api, homeId, device, {
      ...(profile ? { profile } : {}),
      ...(lens?.lens !== undefined ? { lens: lens.lens } : {}),
      ...(lens?.primarySessionId ? { primary_session_id: lens.primarySessionId } : {}),
    });
    return { ticket: s.signaling?.session_ticket ?? '', sessionId: s.session_id };
  };
}
